import React, { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/ui-parts";
import { toast } from "sonner";

export default function Analytics() {
  const [dashboard, setDashboard] = useState(null);
  const [report, setReport] = useState({ unmatched_transactions: [], unmatched_receipts: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, r] = await Promise.all([api.get("/dashboard"), api.get("/analytics/missing")]);
        setDashboard(d.data);
        setReport(r.data);
      } catch { toast.error("Konnte nicht laden"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !dashboard) {
    return (
      <div>
        <PageHeader title="Auswertung" />
        <div className="px-8 py-8 text-sm text-[#5C5E60]">Lade...</div>
      </div>
    );
  }

  const totalReceipts = dashboard.totals.receipts;
  const matchedReceipts = totalReceipts - dashboard.totals.receipts_unmatched;
  const receiptPct = totalReceipts > 0 ? Math.round((matchedReceipts / totalReceipts) * 100) : 0;

  const totalTx = dashboard.totals.transactions;
  const matchedTx = totalTx - dashboard.totals.transactions_unmatched;
  const txPct = totalTx > 0 ? Math.round((matchedTx / totalTx) * 100) : 0;

  // Sum by category
  const catMap = {};
  report.unmatched_receipts.forEach((r) => {
    const k = r.category || "Ohne Kategorie";
    catMap[k] = (catMap[k] || 0) + (r.total_amount || 0);
  });

  return (
    <div>
      <PageHeader
        title="Auswertung"
        subtitle="Vollständigkeit Ihrer Buchhaltung auf einen Blick."
      />

      <div className="px-8 py-8 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E6E6E4] rounded-md p-6">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Belege zugeordnet</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display font-extrabold text-4xl tracking-tight">{receiptPct}%</div>
              <div className="text-sm text-[#5C5E60]">{matchedReceipts} von {totalReceipts}</div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-[#F3F3F1] overflow-hidden">
              <div className="h-full bg-[#18322B] transition-all duration-500" style={{ width: `${receiptPct}%` }} />
            </div>
          </div>
          <div className="bg-white border border-[#E6E6E4] rounded-md p-6">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Buchungen belegt</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display font-extrabold text-4xl tracking-tight">{txPct}%</div>
              <div className="text-sm text-[#5C5E60]">{matchedTx} von {totalTx}</div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-[#F3F3F1] overflow-hidden">
              <div className="h-full bg-[#18322B] transition-all duration-500" style={{ width: `${txPct}%` }} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E6E6E4] rounded-md">
          <div className="border-b border-[#E6E6E4] px-6 py-4">
            <div className="font-display font-bold text-base text-[#1A1A1A]">Fehlende Belege je Firma</div>
            <div className="text-xs text-[#5C5E60] mt-0.5">Buchungen, für die noch ein Beleg gesucht wird</div>
          </div>
          {dashboard.per_company.length === 0 ? (
            <div className="p-6 text-sm text-[#5C5E60]">Keine Firmen angelegt.</div>
          ) : (
            <table className="w-full text-sm" data-testid="analytics-per-company">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8]">
                  <th className="text-left px-6 py-2.5 font-semibold">Firma</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Buchungen</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Ohne Beleg</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Belege</th>
                  <th className="text-right px-6 py-2.5 font-semibold">Ohne Buchung</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.per_company.map((c) => (
                  <tr key={c.id} className="border-t border-[#E6E6E4] hover:bg-[#F9F9F8] transition-colors duration-150">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                        <span className="font-medium text-[#1A1A1A]">{c.name}</span>
                      </div>
                    </td>
                    <td className="text-right px-4 py-3 font-mono-tab">{c.transactions}</td>
                    <td className="text-right px-4 py-3 font-mono-tab">
                      {c.transactions_unmatched > 0 ? (
                        <StatusBadge variant="warning">{c.transactions_unmatched}</StatusBadge>
                      ) : (
                        <StatusBadge variant="success">0</StatusBadge>
                      )}
                    </td>
                    <td className="text-right px-4 py-3 font-mono-tab">{c.receipts}</td>
                    <td className="text-right px-6 py-3 font-mono-tab">
                      {c.receipts_unmatched > 0 ? (
                        <StatusBadge variant="warning">{c.receipts_unmatched}</StatusBadge>
                      ) : (
                        <StatusBadge variant="success">0</StatusBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {Object.keys(catMap).length > 0 && (
          <div className="bg-white border border-[#E6E6E4] rounded-md p-6">
            <div className="font-display font-bold text-base text-[#1A1A1A]">Unzugeordnete Beträge nach Kategorie</div>
            <div className="mt-4 space-y-2">
              {Object.entries(catMap).sort((a, b) => b[1] - a[1]).map(([cat, amount]) => (
                <div key={cat} className="flex items-center justify-between text-sm">
                  <span className="text-[#1A1A1A]">{cat}</span>
                  <span className="font-mono-tab font-semibold">{fmtEUR(amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
